import { yupResolver } from '@hookform/resolvers/yup';
import { Collapse, Stack } from '@mui/material';
import { useAtomValue } from 'jotai';
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  ControllerRenderProps,
  FormProvider,
  Resolver,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { FieldFactory } from '../../../components/Form/FieldFactory';
import { FormFieldsContainer } from '../../../components/Form/FormComponents';
import { FormFieldWithLabelFactory } from '../../../components/Form/FormFieldAndLabelFactory';
import { StickyFormButtons } from '../../../components/Form/StickyFormButtons';
import { CompanyType } from '../../../data-models/company.data-model';
import { useFocusCallback, useFocusElement } from '../../../hooks/useFocusElement';
import {
  CompanySearchResponse,
  createCompanySearchResponse,
} from '../../../schemas/CompanySearchResponse.schema';
import { portcosByNameMapAtomJ } from '../../../services/state/PortCosStateJ';
import { schemaToFormFields } from '../../../util/schema-utils';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { ReservesAction, selectedFundInvestmentsByCompanyNameState } from '../state/FPState';
import {
  AddInvestmentSchema,
  companyField,
  hypotheticalCompanyFields,
  useGetInvestmentSchemaForCompany,
} from './addInvestmentFormUtils';
import { useReservesActions } from './reservesActions';
import { useReservesFormState } from './useReservesFormState';

interface IFutureInvestmentFormProps {
  action: ReservesAction;
  defaultValues?: Partial<AddInvestmentSchema>;
}
export function FutureInvestmentForm({ defaultValues, action }: IFutureInvestmentFormProps) {
  const { loading, loadingAddAnother, methods, onSubmit, resetFormState } = useFutureInvestmentForm({
    defaultValues,
    action,
  });

  return (
    <FormProvider {...methods}>
      <FormFieldsContainer key={String(loadingAddAnother)} style={{ paddingBlock: '0.1rem' }}>
        {!defaultValues?.company && <CompanyFields />}
        <FutureTransactionFields action={action} />
      </FormFieldsContainer>
      <StickyFormButtons
        onCancel={resetFormState}
        loading={loading}
        onSubmit={() => onSubmit(false)}
        secondaryActionProps={
          action == ReservesAction.EditTransaction
            ? undefined
            : {
                label: 'Submit and Add Another',
                onClick: () => onSubmit(true),
                loading: loadingAddAnother,
              }
        }
      />
    </FormProvider>
  );
}

function CompanyFields() {
  const { control, setValue, resetField } = useFormContext<AddInvestmentSchema>();
  const portcosByName = useAtomValue(portcosByNameMapAtomJ);
  const investmentsByCompanyName = useAtomValue(selectedFundInvestmentsByCompanyNameState);

  const onChange = useCallback(
    (company: CompanySearchResponse) => {
      const investment = investmentsByCompanyName.get(company.fields.name);
      setValue('company', company);
      setValue('description', investment?.description ?? null, { shouldDirty: true, shouldValidate: true });
      setValue('sectorId', investment?.sectorId ?? null, { shouldDirty: true, shouldValidate: true });
    },
    [investmentsByCompanyName, setValue]
  );

  const companySelectField = {
    ...companyField,
    rendererMeta: {
      ...companyField.rendererMeta,
      onAdd: (companyName: string) => {
        const id = portcosByName.get(companyName)?.id ?? '';
        onChange(
          createCompanySearchResponse({
            id,
            fields: {
              name: companyName,
            },
          } as CompanySearchResponse)
        );
      },
    },
  };

  const company = useWatch({ control, name: 'company' });
  const companySelectFormProps = {
    onChange,
    value: company,
  } as ControllerRenderProps;

  useFocusElement('company-select', 'input');

  const showHypotheticalFields =
    company?.fields?.name != undefined && !portcosByName.get(company?.fields?.name ?? '');
  const hypotheticalFields = useMemo(() => {
    return hypotheticalCompanyFields();
  }, []);

  useEffect(() => {
    if (!showHypotheticalFields) {
      resetField('description');
      resetField('sectorId');
    }
  }, [showHypotheticalFields, resetField]);

  return (
    <>
      <div id='company-select'>
        <FieldFactory formField={companySelectField} formProps={companySelectFormProps} />
        {showHypotheticalFields && (
          <Stack mt='1rem' gap='1rem'>
            {hypotheticalFields.map((field) => (
              <Fragment key={field.key}>
                <FormFieldWithLabelFactory key={field.key} formField={field} />
              </Fragment>
            ))}
          </Stack>
        )}
      </div>
    </>
  );
}

function FutureTransactionFields({ action }: { action: ReservesAction }) {
  const { fields, autoFocus } = useSyncFieldsWithCompany({ action });
  const focus = useFocusCallback();
  useLayoutEffect(() => {
    if (!fields.length || !autoFocus) {
      return;
    }
    focus('transaction-fields', 'input');
  }, [fields.length, focus, autoFocus]);

  return (
    <Collapse in={fields.length > 0} id='transaction-fields'>
      {!fields.length ? (
        <div />
      ) : (
        fields.map((field) => (
          <Fragment key={field.key}>
            <FormFieldWithLabelFactory key={field.key} formField={field} />
            <br />
          </Fragment>
        ))
      )}
    </Collapse>
  );
}

function useGetInvestmentResolver() {
  const getSchema = useGetInvestmentSchemaForCompany();
  const resolver: Resolver<AddInvestmentSchema> = useCallback(
    (values: AddInvestmentSchema, context, options) => {
      const { company } = values;
      const schema = getSchema(company) as ObjectSchema<AddInvestmentSchema>;
      return yupResolver(schema)(values, context, options);
    },
    [getSchema]
  );

  return resolver;
}

function useSyncFieldsWithCompany({ action }: { action: ReservesAction }) {
  const methods = useFormContext<AddInvestmentSchema>();
  const { setValue } = methods;

  const company = useWatch({ control: methods.control, name: 'company' });

  const getSchema = useGetInvestmentSchemaForCompany();

  const fields = useMemo(() => {
    if (!company) {
      return [];
    }
    const schema = getSchema(company);
    return schemaToFormFields(schema, ['amount', 'date']);
  }, [company, getSchema]);

  useEffect(() => {
    if (!company || action == ReservesAction.EditTransaction) {
      return;
    }
    const schema = getSchema(company);
    const { type } = schema.getDefault();
    setValue('type', type);
  }, [company, getSchema, setValue, action]);

  const autoFocus = methods.getValues('company.fields.type') === CompanyType.portfolio;

  return { fields, autoFocus };
}

function useFutureInvestmentForm({ defaultValues, action }: IFutureInvestmentFormProps) {
  const { resetFormState } = useReservesFormState();
  const { addInvestment, editInvestmentTransaction } = useReservesActions();
  const [loading, setLoading] = useState(false);
  const [loadingAddAnother, setLoadingAddAnother] = useState(false);
  const getSchema = useGetInvestmentSchemaForCompany();

  const resolver = useGetInvestmentResolver();
  const methods = useForm<AddInvestmentSchema>({
    defaultValues,
    resolver,
  });

  const onSubmit = useCallback(
    async (addAnother = false) => {
      const isValid = await methods.trigger();
      if (isValid) {
        if (addAnother) {
          setLoadingAddAnother(true);
        } else {
          setLoading(true);
        }
        const { company, description, sectorId, ...rest } = methods.getValues();
        let callback;
        switch (action) {
          case ReservesAction.AddInvestment:
          case ReservesAction.AddTransaction:
            callback = addInvestment;
            break;
          case ReservesAction.EditTransaction:
            callback = editInvestmentTransaction;
            break;
        }
        if (!callback) {
          console.error(new Error('No callback found for action'));
          return false;
        }
        await callback({
          company,
          description,
          sectorId,
          transaction: {
            ...rest,
          },
        });
        if (addAnother) {
          setLoadingAddAnother(false);
          const schema = getSchema(company);
          methods.reset({ ...schema.getDefault(), company } as unknown as AddInvestmentSchema);
        } else {
          setLoading(false);
          resetFormState();
        }
      } else {
        captureFormValidationError('Future investment', methods.formState.errors);
        return false;
      }
    },
    [action, addInvestment, editInvestmentTransaction, getSchema, methods, resetFormState]
  );
  return { loading, loadingAddAnother, methods, onSubmit, resetFormState };
}
